import type { CookieHelpers } from '@/utils/cookie';
import { isForeignEnvironment } from '../index';
import {
	consumeLaunchData,
	parseAuthToken,
	validString,
} from './parse-app-data';
import { persistAppData } from './persist-app-data';
import { persistSession } from './persist-session';
import { readAppData } from './read-app-data';
import { readAuthToken } from './read-auth-token';
import type { BridgeOptions, NativeIdentity } from './types';

export type { NativeDataError } from './types';

let identity: NativeIdentity | undefined;
let isSessionSynced = false;
let isSessionChanged = false;

export const getNativeClientIdentity = (): NativeIdentity | undefined =>
	identity;
export const hasSyncedNativeSession = (): boolean => isSessionSynced;
export const hasNativeSessionChanged = (): boolean => isSessionChanged;

/** Call before the login SDK is created. The caller owns route scope/lifecycle. */
export async function syncAppData({
	cookies,
	onError,
}: BridgeOptions & { cookies: CookieHelpers }): Promise<void> {
	try {
		const query = consumeLaunchData();
		// In parallel, so a reply-capable iOS data bridge cannot add a second
		// two-second wait before requesting auth. Late results never mutate state.
		const [nativeData, nativeToken] = await Promise.all([
			readAppData({ onError }),
			readAuthToken({ onError }),
		]);
		cookies.syncCookies();
		const data = nativeData.clientId ? nativeData : { ...query, ...nativeData };
		const clientId =
			data.clientId || validString(cookies.getCookie('clientId'));
		identity = persistAppData({ cookies, data });
		const token =
			nativeToken ||
			(clientId && isForeignEnvironment(clientId)
				? parseAuthToken(data.Authorization)
				: undefined);
		if (token) {
			isSessionChanged = persistSession({ cookies, token });
			isSessionSynced = true;
		}
	} catch {
		onError({
			stage: 'storage',
			reason: 'Unable to persist native app context',
		});
	}
}
